"use client";

import React from "react";
import type { StaticImageData } from "next/image";

interface StatItem {
  value: string;
  description: string;
}

interface GrowthStoryProps {
  title: React.ReactNode;
  card1: StatItem;
  card2: StatItem;
  card3: StatItem;
  card4: StatItem;
  card5: StatItem;
  card6: StatItem;
  bgImage?: string | StaticImageData;
  globeImage?: string | StaticImageData;
  patternImage?: string | StaticImageData;
}

const cardStyle: React.CSSProperties = {
  borderRadius: "20px",
  background:
    "linear-gradient(139deg, rgba(0, 0, 0, 0.30) 2.24%, rgba(34, 91, 212, 0.40) 98.25%)",
  boxShadow: "-1px -1px 0 0 rgba(34, 91, 212, 0.40)",
};

function getImgSrc(src?: string | StaticImageData) {
  if (!src) return undefined;
  return typeof src === "string" ? src : src.src;
}

function StatCard({
  value,
  description,
  className = "",
  accentImage,
}: StatItem & {
  className?: string;
  accentImage?: string;
}) {
  return (
    <div
      style={cardStyle}
      className={`group relative flex flex-col justify-start gap-3
        p-5 sm:p-6 lg:p-8
        min-h-[120px] sm:min-h-[200px] xl:min-h-0
        overflow-hidden
        transition-all duration-500 ease-out
        hover:-translate-y-2 hover:scale-[1.02]
        hover:shadow-[0_20px_50px_rgba(34,91,212,0.25)]
        ${className}`}
    >
      {/* Hover glow */}
      <div
        className="
          pointer-events-none absolute inset-0
          opacity-0 group-hover:opacity-100
          transition-opacity duration-500
          bg-[radial-gradient(circle_at_30%_20%,rgba(34,91,212,0.25),transparent_60%)]
        "
      />

      {accentImage && (
        <div
          className="
            pointer-events-none absolute hidden md:block
            transition-all duration-700 ease-out
            group-hover:scale-110
            group-hover:opacity-100
          "
          style={{
            bottom: "-1rem",
            right: "-1rem",
            width: "17rem",
            height: "12rem",
            backgroundImage: `url(${accentImage})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        />
      )}

      <div className="relative z-[1] flex flex-col gap-2 sm:gap-3">
        <span
          className="
            clash-font font-semibold text-white
            text-[30px] sm:text-[36px] lg:text-[42px]
            leading-none tracking-tight
            transition-transform duration-500
            group-hover:translate-x-1
          "
        >
          {value}
        </span>

        <p
          className="
            text-[#ECF0F6]
            text-[13px] sm:text-[14px] lg:text-[17px]
            leading-[150%] lg:leading-[160%]
            max-w-[95%]
          "
        >
          {description}
        </p>
      </div>
    </div>
  );
}

export default function GrowthStory({
  title,
  card1,
  card2,
  card3,
  card4,
  card5,
  card6,
  bgImage,
  globeImage,
  patternImage,
}: GrowthStoryProps) {
  const bg = getImgSrc(bgImage);
  const globeSrc = getImgSrc(globeImage);
  const patternSrc = getImgSrc(patternImage);

  return (
    <section
      className="relative overflow-hidden bg-[#020617] z-1"
      style={
        bg
          ? {
              backgroundImage: `url(${bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : undefined
      }
    >
      <div
        className="
          relative z-10 max-w-7xl mx-auto
          flex flex-col
          gap-8 sm:gap-10 lg:gap-[50px]
          py-[60px] sm:py-[80px] lg:py-[120px]
          px-4 sm:px-6
        "
      >
        {/* Title */}
        <h2
          className="
            clash-font font-medium text-center text-white
            text-[length:var(--section-title)]
            leading-[120%]
            px-2
          "
        >
          {title}
        </h2>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 xl:grid-rows-2 gap-4 sm:gap-5">
          <StatCard {...card1} />

          <StatCard
            {...card2}
            className="xl:row-span-2"
            accentImage={globeSrc}
          />

          <StatCard {...card3} />
          <StatCard {...card4} />

          <StatCard {...card5} />

          <StatCard
            {...card6}
            className="xl:col-span-2"
            accentImage={patternSrc}
          />
        </div>
      </div>
    </section>
  );
}
